import React, { memo } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  Plus, 
  Search, 
  Clock,
  Users,
  FileText,
  Folder,
  Star,
  Settings,
  MoreHorizontal
} from 'lucide-react';

const NotesContent = memo(({ activity }) => {
  const isTyping = activity?.action === 'type';
  const isScrolling = activity?.action === 'scroll';
  const isClicking = activity?.action === 'click';
  
  const [actionCount, setActionCount] = React.useState(0);
  React.useEffect(() => {
    if (activity) {
      setActionCount(prev => prev + 1);
    }
  }, [activity]);
  
  const showDifferentPage = actionCount > 3;

  const sidebarPages = [
    { icon: '🏠', name: 'Home', indent: 0 },
    { icon: '📋', name: 'Team Meetings', indent: 0, active: true },
    { icon: '📊', name: 'Product Roadmap', indent: 0 },
    { icon: '🎯', name: 'OKRs Q1 2024', indent: 0 },
    { icon: '📝', name: 'Design System', indent: 0, expanded: true },
    { icon: '🎨', name: 'Components', indent: 1 },
    { icon: '🖼️', name: 'Guidelines', indent: 1 },
    { icon: '💼', name: 'Projects', indent: 0, expanded: true },
    { icon: '🚀', name: 'FluxSpaces v2.0', indent: 1 },
    { icon: '📱', name: 'Mobile App', indent: 1 },
  ];

  return (
    <div className="notion-content">
      {/* Sidebar */}
      <div className="notion-sidebar">
        <div className="notion-workspace">
          <div className="workspace-header">
            <img src="/logo.png" alt="FluxSpace" className="workspace-logo" />
            <span className="workspace-name">FluxSpace Team</span>
            <ChevronDown size={8} />
          </div>
        </div>

        <div className="notion-search">
          <Search size={8} />
          <span>Search...</span>
        </div>

        <div className="notion-nav">
          <div className="nav-section">
            {sidebarPages.map((page, i) => (
              <div 
                key={i} 
                className={`notion-page-item ${page.active ? 'active' : ''}`}
                style={{ paddingLeft: `${8 + page.indent * 12}px` }}
              >
                {page.expanded !== undefined && (
                  page.expanded ? <ChevronDown size={6} /> : <ChevronRight size={6} />
                )}
                <span className="page-icon">{page.icon}</span>
                <span className="page-name">{page.name}</span>
              </div>
            ))}
          </div>

          <div className="sidebar-footer">
            <div className="sidebar-btn">
              <Plus size={8} />
              <span>New Page</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`notion-main ${isScrolling ? 'scrolling' : ''}`}>
        {/* Page Header */}
        <div className="notion-header">
          <div className="page-breadcrumb">
            <span className="breadcrumb-item">FluxSpace Team</span>
            <ChevronRight size={6} />
            <span className="breadcrumb-item">Team Meetings</span>
          </div>
          <div className="page-actions">
            <button className="notion-btn"><MoreHorizontal size={8} /></button>
            <button className="notion-btn primary">Share</button>
          </div>
        </div>

          {/* Page Content */}
          <div className="notion-page">
            <div className="page-title-section">
              <span className="page-emoji">{showDifferentPage ? '📊' : '📋'}</span>
              <h1 className="page-title">{showDifferentPage ? 'Product roadmap' : 'Team Meetings'}</h1>
            </div>

          <div className="page-properties">
            <div className="property">
              <span className="property-label">Owner</span>
              <div className="property-value">
                <span className="avatar">{showDifferentPage ? '👩‍💻' : '👩‍💼'}</span>
                <span>{showDifferentPage ? 'Alex Rodriguez' : 'Sarah Chen'}</span>
                <span>Sarah Chen</span>
              </div>
            </div>
            <div className="property">
              <span className="property-label">Last edited</span>
              <span className="property-value">2 hours ago</span>
            </div>
          </div>

          {/* Meeting Note */}
          <div className="notion-block">
            <h2 className="block-heading">Weekly Sync - Jan 3, 2024</h2>
          </div>

          <div className="notion-block">
            <div className="block-label">📅 Date & Time</div>
            <div className="block-text">Thursday, January 3, 2024 • 10:00 AM - 11:00 AM</div>
          </div>

          <div className="notion-block">
            <div className="block-label">👥 Attendees</div>
            <div className="attendees-list">
              <span className="attendee">👩‍💼 Sarah Chen</span>
              <span className="attendee">👨‍💻 Alex Rodriguez</span>
              <span className="attendee">👩‍🔬 Maya Patel</span>
              <span className="attendee">👨‍🎨 Jordan Kim</span>
            </div>
          </div>

          <div className="notion-block">
            <h3 className="block-subheading">Agenda</h3>
            <div className="checklist">
              <div className="checklist-item checked">
                <div className="checkbox checked">✓</div>
                <span>Product roadmap review</span>
              </div>
              <div className="checklist-item checked">
                <div className="checkbox checked">✓</div>
                <span>Q1 OKR progress update</span>
              </div>
              <div className="checklist-item">
                <div className="checkbox"></div>
                <span>Design system migration timeline</span>
              </div>
              <div className="checklist-item">
                <div className="checkbox"></div>
                <span>Mobile app beta feedback</span>
              </div>
            </div>
          </div>

          <div className="notion-block">
            <h3 className="block-subheading">Key Discussion Points</h3>
            <div className="block-text">
              <p><strong>FluxSpaces v2.0 Launch</strong></p>
              <p>• Target launch date: February 15, 2024</p>
              <p>• Beta testing phase starts next week</p>
              <p>• Marketing campaign ready to go</p>
            </div>
          </div>

          <div className="notion-block">
            <div className="block-text">
              <p><strong>Design System Migration</strong></p>
              <p>• 60% of components migrated to new system</p>
              <p>• Documentation being updated in parallel</p>
              <p>• Team training session scheduled for Jan 10</p>
            </div>
          </div>

          <div className="notion-block">
            <h3 className="block-subheading">Action Items</h3>
            <div className="action-items">
              <div className="action-item">
                <div className="action-checkbox"></div>
                <div className="action-content">
                  <span className="action-text">Finalize beta user list</span>
                  <div className="action-meta">
                    <span className="action-assignee">👩‍💼 Sarah</span>
                    <span className="action-due">Due: Jan 5</span>
                  </div>
                </div>
              </div>
              <div className="action-item">
                <div className="action-checkbox"></div>
                <div className="action-content">
                  <span className="action-text">Update API documentation</span>
                  <div className="action-meta">
                    <span className="action-assignee">👨‍💻 Alex</span>
                    <span className="action-due">Due: Jan 8</span>
                  </div>
                </div>
              </div>
              <div className="action-item">
                <div className="action-checkbox"></div>
                <div className="action-content">
                  <span className="action-text">Prepare design system workshop</span>
                  <div className="action-meta">
                    <span className="action-assignee">👩‍🔬 Maya</span>
                    <span className="action-due">Due: Jan 9</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showDifferentPage ? (
            <div className="notion-block">
              <h2 className="block-heading">Q1 2024 Roadmap</h2>
              <div className="block-text">
                <p><strong>🚀 January - February</strong></p>
                <p>• Launch FluxSpaces v2.0 with new dashboard</p>
                <p>• Complete mobile app optimization</p>
                <p>• API v2 migration</p>
              </div>
              {isTyping && (
                <div className="block-text editable typing">
                  <p><strong>📱 March</strong></p>
                  <p>• Beta testing for mobile features</p>
                  <p>• User feedback integration{isTyping && <span className="notion-cursor">|</span>}</p>
                </div>
              )}
            </div>
          ) : (
            <div className={`notion-block ${isTyping ? 'typing' : ''}`}>
              <h3 className="block-subheading">Notes</h3>
              <div className="block-text editable">
                <p>Great energy in the meeting today! Team is excited about the v2.0 launch.</p>
                {isTyping && <span className="notion-cursor">|</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

NotesContent.displayName = 'NotesContent';

export default NotesContent;
